// 변경 항목 ↔ 원 계약 견적 줄 — 연결 검증·표기 사본·감액 잔량 대조를 짓는 한 벌(인메모리·Supabase 가 같이 쓴다 · 저장소를 읽지 않는다).

import { DeductOverRemainingError, QuoteSourceScopeError } from "./errors";
import type { ChangeOrder, ChangeOrderItem } from "./types";
import type { Contract } from "../contract/rows";
import { spaceLabelLookup, spaceLabelsFrom } from "../org/space-label";
import type { QuoteItem, QuoteTrade } from "../quote/types";

export type SourceTrade = Pick<QuoteTrade, "id" | "org_id" | "quote_id" | "name" | "sort_order">;

export type SourceItem = Pick<
  QuoteItem,
  | "id"
  | "org_id"
  | "quote_id"
  | "quote_trade_id"
  | "parent_id"
  | "sort_order"
  | "scope"
  | "space_code"
  | "item"
  | "unit"
  | "qty"
  | "material_unit_price"
  | "labor_unit_price"
  | "amount"
>;

type SourceLine = Pick<ChangeOrderItem, "direction" | "qty" | "amount" | "source_quote_item_id">;

export interface QuoteSourceContext {
  org_id: string;
  quote_id: string;
  trades: readonly SourceTrade[];
  items: readonly SourceItem[];
  spaceLabel: (code: string) => string | null;
  // 같은 계약에서 이미 승인된(자기 자신 제외) 변경공사의 연결된 줄.
  accepted: readonly SourceLine[];
}

export interface SourceFields {
  source_quote_item_id: string | null;
  quote_trade_id: string | null;
  space_code: string | null;
  space_label: string | null;
  trade_name: string | null;
  source_label: string | null;
}

export const NO_SOURCE: SourceFields = {
  source_quote_item_id: null,
  quote_trade_id: null,
  space_code: null,
  space_label: null,
  trade_name: null,
  source_label: null,
};

// 줄 하나라도 원 견적 줄을 가리키는지 — 아니면 저장소는 견적을 읽지 않는다(옛 행·「추가 항목」 만의 변경공사).
export const hasLinked = (lines: readonly { source_quote_item_id?: string | null }[]): boolean =>
  lines.some((l) => !!l.source_quote_item_id);

export interface SourceContextInput {
  contract: Pick<Contract, "org_id" | "quote_id">;
  trades: readonly SourceTrade[];
  items: readonly SourceItem[];
  spaceSetting: string | null | undefined;
  siblings: readonly Pick<ChangeOrder, "id" | "status">[];
  siblingItems: readonly (SourceLine & Pick<ChangeOrderItem, "change_order_id">)[];
  excludeId?: string | null;
}

// 두 저장소가 읽은 행으로 같은 문맥을 짓는다 — 다른 조직·다른 견적 행은 여기서 한 번 더 걸러낸다.
export const buildSourceContext = (input: SourceContextInput): QuoteSourceContext => {
  const { org_id, quote_id } = input.contract;
  const accepted = new Set(
    input.siblings.filter((s) => s.status === "ACCEPTED" && s.id !== input.excludeId).map((s) => s.id)
  );
  return {
    org_id,
    quote_id,
    trades: input.trades.filter((t) => t.org_id === org_id && t.quote_id === quote_id),
    items: input.items.filter((i) => i.org_id === org_id && i.quote_id === quote_id),
    spaceLabel: spaceLabelLookup(input.spaceSetting),
    accepted: input.siblingItems.filter((i) => accepted.has(i.change_order_id) && !!i.source_quote_item_id),
  };
};

const clip = (value: string, max: number): string => value.trim().slice(0, max);

// 연결 줄 단위 = 원 견적 줄 단위(저장·고르기 목록이 같은 값을 쓴다).
const sourceUnitOf = (item: Pick<SourceItem, "unit">): string => clip(item.unit, 20);

export interface QuoteItemTree {
  // 부모를 따라가다 자기로 돌아오는 줄(A↔B · 자기 부모) — 꼬리(C→A↔B)의 C 는 들지 않는다.
  cyclic: ReadonlySet<string>;
  // 순환 안의 자식은 자식으로 치지 않는다 — 순환 멤버는 서로를 부모로 가리켜도 각자 잎이다.
  hasChildren: (id: string) => boolean;
}

// 견적 줄 부모 관계 한 벌 — 고르기 목록·연결 검증·현재 내역서가 같은 잎을 본다(순환 줄은 셋 다 잎으로 고를 수 있다).
export const quoteItemTreeOf = (items: readonly Pick<SourceItem, "id" | "parent_id">[]): QuoteItemTree => {
  const byId = new Map(items.map((i) => [i.id, i]));
  const cyclic = new Set<string>();
  for (const item of items) {
    const seen = new Set<string>();
    let cur: Pick<SourceItem, "id" | "parent_id"> | undefined = item;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    if (cur?.id === item.id) cyclic.add(item.id);
  }
  const parents = new Set(items.filter((c) => c.parent_id && !cyclic.has(c.id)).map((c) => c.parent_id));
  return { cyclic, hasChildren: (id) => parents.has(id) };
};

const isLeaf = (items: readonly SourceItem[], id: string): boolean => !quoteItemTreeOf(items).hasChildren(id);

export const sourceLabelOf = (item: Pick<SourceItem, "scope" | "item">, tradeName: string): string =>
  clip([item.scope.trim(), item.item.trim()].filter(Boolean).join(" ") || tradeName || "견적 줄", 500);

const findSource = (ctx: QuoteSourceContext | null, id: string): { item: SourceItem; trade: SourceTrade } => {
  const item = ctx?.items.find((i) => i.id === id);
  const trade = item ? ctx?.trades.find((t) => t.id === item.quote_trade_id) : undefined;
  if (!ctx || !item || !trade || !isLeaf(ctx.items, item.id)) throw new QuoteSourceScopeError();
  return { item, trade };
};

// 연결 줄의 단위 — 원 견적 줄 단위를 그대로 쓴다(다르면 내역서 수량을 더할 수 없고 감액 잔량도 잴 수 없다).
export interface ResolvedSource extends SourceFields {
  unit?: string;
}

// 연결 id 하나로 표기 사본·단위까지 채운다 — 화면이 보낸 표기·단위는 믿지 않고 원 견적 줄에서 다시 짓는다.
export const resolveSource = (
  line: { source_quote_item_id?: string | null },
  ctx: QuoteSourceContext | null
): ResolvedSource => {
  if (!line.source_quote_item_id) return NO_SOURCE;
  const { item, trade } = findSource(ctx, line.source_quote_item_id);
  const space = item.space_code ? clip(item.space_code, 60) : "";
  return {
    source_quote_item_id: item.id,
    quote_trade_id: trade.id,
    space_code: space || null,
    // 설정에 없는 코드는 표기 사본을 비운다 — 코드 원문이 고객 문서·내역서에 라벨로 나가지 않게.
    space_label: space && ctx ? clip(ctx.spaceLabel(space) ?? "", 100) || null : null,
    trade_name: clip(trade.name, 100) || null,
    source_label: sourceLabelOf(item, trade.name),
    unit: sourceUnitOf(item),
  };
};

// 수량은 소수 셋째 자리(numeric(14,3))까지라 정수로 올려 더한다.
const milli = (qty: number): number => Math.round(qty * 1000);

// 연결된 감액 합이 원 견적 줄 + 앞서 승인된 추가 − 앞서 승인된 감액을 넘으면 DeductOverRemainingError.
export const assertDeductWithinRemaining = (
  items: readonly SourceLine[],
  ctx: QuoteSourceContext | null
): void => {
  const used = new Map<string, { qty: number; amount: number }>();
  for (const line of items) {
    if (line.direction !== "DEDUCT" || !line.source_quote_item_id) continue;
    const sum = used.get(line.source_quote_item_id) ?? { qty: 0, amount: 0 };
    used.set(line.source_quote_item_id, { qty: sum.qty + milli(line.qty), amount: sum.amount + line.amount });
  }
  for (const [id, sum] of used) {
    const { item, trade } = findSource(ctx, id);
    let qty = milli(item.qty);
    let amount = item.amount;
    for (const prev of ctx?.accepted ?? []) {
      if (prev.source_quote_item_id !== id) continue;
      const sign = prev.direction === "ADD" ? 1 : -1;
      qty += sign * milli(prev.qty);
      amount += sign * prev.amount;
    }
    if (sum.qty > qty || sum.amount > amount) {
      throw new DeductOverRemainingError(
        id,
        sourceLabelOf(item, trade.name),
        Math.max(qty, 0) / 1000,
        Math.max(amount, 0),
        item.unit
      );
    }
  }
};

// 편집기 「견적 줄에서 고르기」 한 줄 — 화면(클라이언트)으로 넘기는 모양.
export interface SourcePickerItem {
  id: string;
  label: string;
  space_label: string | null;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  amount: number;
}

export interface SourcePickerTrade {
  id: string;
  name: string;
  items: SourcePickerItem[];
}

// 계약 견적의 공종 → 맨 끝 항목(SET 부모는 자기 단가가 없어 뺀다) — 공종·항목 순서는 견적 그대로.
export const pickerTradesOf = (
  quote: { trades: readonly SourceTrade[]; items: readonly SourceItem[] },
  spaceSetting: string | null | undefined
): SourcePickerTrade[] => {
  const spaceLabel = spaceLabelsFrom(spaceSetting);
  const tree = quoteItemTreeOf(quote.items);
  return [...quote.trades]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((trade) => ({
      id: trade.id,
      name: trade.name,
      items: quote.items
        .filter((i) => i.quote_trade_id === trade.id && !tree.hasChildren(i.id))
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((i) => ({
          id: i.id,
          label: sourceLabelOf(i, trade.name),
          space_label: i.space_code ? spaceLabel(i.space_code) : null,
          item: clip(i.item, 200) || clip(sourceLabelOf(i, trade.name), 200),
          unit: sourceUnitOf(i),
          qty: i.qty,
          material_unit_price: i.material_unit_price,
          labor_unit_price: i.labor_unit_price,
          amount: i.amount,
        })),
    }))
    .filter((t) => t.items.length > 0);
};
